import { useQuery } from '@tanstack/react-query';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useLocation, useParams } from 'react-router-dom';
import styled from 'styled-components';

import { bankAccountGetBankAccount } from '@column/api';
import {
  AmountInput,
  Button,
  Chip,
  CurrenciesUSD,
  Dropdown,
  DropdownOption,
  Icon,
  Input,
  NumberInput,
  ObjectCard,
  Toggle,
  Tooltip,
} from '@column/column-ui-kit';

import { ROUTE } from '~/app/routes';
import { CopyInput, Loading, RenderFields } from '~/components';
import { BankAccountDropdown } from '~/components/fields/BankAccountDropdown';
import { useGetPlatformIntlWireConfig } from '~/hooks';
import { useAccountNumber, useBankAccount } from '~/hooks/useBankAccounts';
import { FeatureFlag, useFeatureFlag } from '~/lib/flags';
import { useNavigate } from '~/lib/navigation';
import { EntityRepository, EntityWithDetails } from '~/repositories/EntityRepository';
import { InternationalWireBillingConfig, PlatformRepository } from '~/repositories/PlatformRepository';
import { useNotificationStore } from '~/stores/Notification';
import { useSessionStore } from '~/stores/Session';
import { FormText, Inner } from '~/styles';
import { ApiError } from '~/typings/API';
import { ApiErrCode } from '~/typings/enum';
import { formatDateTime, formatString } from '~/util';

interface Params {
  id?: string;
}

interface PlatformSettingsFields {
  // Root Entity
  rootEntity?: never;

  // Platform Settings
  createdAt?: Date;
  id: string;
  name: string;
  platformLevel: string;
  reportingTimeZone?: string;

  // International Wire Config
  revenueAccountNumberId?: string;
  fixedFee?: number;
  fxRateMarginBps?: number;

  // Transfer Approval Config
  enabledForAchCredits?: boolean;
  enabledForAchDebits?: boolean;
  enabledForCheckDebits?: boolean;
  enabledForInternationalWire?: boolean;
  enabledForWire?: boolean;
  enabledForRealtime?: boolean;
}

const Action = styled.div`
  display: flex;
  justify-content: flex-end;
`;

const StyledAmountInput = styled(AmountInput)`
  > div:first-child {
    gap: 2px;
  }
`;

const ToggleTooltipTarget = styled.span`
  display: inline-flex;
`;

const getEntityName = (entity?: EntityWithDetails) => {
  if (!entity) return undefined;
  if (entity.personDetails)
    return `${entity.personDetails.firstName || ''} ${entity.personDetails.lastName || ''}`.trim();
  if (entity.businessDetails) return entity.businessDetails.businessName;
  return undefined;
};

const getEntityIcon = (entity?: EntityWithDetails): keyof typeof Icon | undefined => {
  if (!entity) return undefined;
  return entity.type === 'PERSON' ? 'User' : 'Business';
};

const TRANSFER_APPROVALS_SUPPORT_TOOLTIP = 'Contact Column support to configure transfer approvals for this platform.';

export const PagePlatformSettings: React.FC = () => {
  const { currentUser, currentPlatform, loadPlatform, isSandbox } = useSessionStore();
  const location = useLocation();
  const { id } = useParams<keyof Params>() as Params;
  const navigate = useNavigate();
  const platformId = useMemo(() => {
    if (location.pathname === ROUTE.PLATFORM_SETTINGS && currentUser?.defaultPlatformId) {
      return currentUser.defaultPlatformId;
    }
    return id;
  }, [currentPlatform, currentUser, id]);

  const enableTransferApprovals = useFeatureFlag(FeatureFlag.EnableTransferApprovals);
  const enableRealtimeTransferApprovals = useFeatureFlag(FeatureFlag.EnableRealtimeTransferApprovals);
  const transferApprovalConfigLocked = currentPlatform?.transferApprovalConfig?.locked === true;
  const canConfigureTransferApprovals = !transferApprovalConfigLocked;

  const { addSuccessNotification, addDangerNotification } = useNotificationStore();
  const {
    response: revenueBankAccountResponse,
    isLoading: isRevenueBankAccountLoading,
    createRequest: getRevenueBankAccount,
  } = useBankAccount();
  const { createRequest: getRevenueAccountNumber } = useAccountNumber({
    onSuccess: (d) => getRevenueBankAccount({ id: d.bankAccountId, platformId }),
  });
  const [intlWireConfig, setIntlWireConfig] = useState<InternationalWireBillingConfig>();
  const { createRequest: getPlatformIntlWireConfig, isLoading: isIntlWireConfigLoading } = useGetPlatformIntlWireConfig(
    {
      onSuccess: (data) => {
        setIntlWireConfig(data);
        if (data.revenueAccountNumberId) {
          getRevenueAccountNumber({ id: data.revenueAccountNumberId, platformId });
        }
      },
      onError: (error) => {
        addDangerNotification({
          content: error.message ?? 'Something went wrong getting International Wire Billing Config, please try again.',
        });
      },
    }
  );

  const [timezones, setTimezones] = useState<DropdownOption[]>([]);

  const {
    data: rootEntity,
    isLoading: isRootEntityLoading,
    error: rootEntityError,
  } = useQuery<EntityWithDetails | null>({
    queryKey: [currentPlatform?.id, 'platform', 'root-entity', isSandbox],
    queryFn: () => EntityRepository.getRoot(currentPlatform?.id ?? '', isSandbox ? 'sandbox' : 'live'),
    enabled: !!currentPlatform?.id,
    staleTime: 5 * 60 * 1000,
    retry: (failureCount: number, error: unknown) => {
      const apiError = error as Partial<ApiError> | undefined;
      if (apiError?.code === ApiErrCode.ResourceNotFound.toString()) {
        return false;
      }
      return failureCount < 3;
    },
  });
  const rootEntityNotFound =
    rootEntityError && (rootEntityError as Partial<ApiError>).code === ApiErrCode.ResourceNotFound.toString();

  const formMethods = useForm<PlatformSettingsFields>({
    defaultValues: {
      createdAt: currentPlatform?.createdAt,
      name: currentPlatform?.name,
      platformLevel: currentPlatform?.platformLevel,
      reportingTimeZone: currentPlatform?.reportingTimeZone,
      enabledForCheckDebits: currentPlatform?.transferApprovalConfig?.enabledForCheckDebits ?? false,
      enabledForInternationalWire: currentPlatform?.transferApprovalConfig?.enabledForInternationalWire ?? false,
      enabledForWire: currentPlatform?.transferApprovalConfig?.enabledForWire ?? false,
    },
  });
  const { handleSubmit, setValue } = formMethods;
  useEffect(() => {
    if (!currentPlatform) return;
    setValue('createdAt', currentPlatform.createdAt);
    setValue('id', currentPlatform.id);
    setValue('name', currentPlatform.name ?? '');
    setValue('platformLevel', currentPlatform.platformLevel ?? '');
    setValue('reportingTimeZone', currentPlatform.reportingTimeZone ?? '');
    setValue('enabledForAchCredits', currentPlatform.transferApprovalConfig?.enabledForAchCredits ?? false);
    setValue('enabledForAchDebits', currentPlatform.transferApprovalConfig?.enabledForAchDebits ?? false);
    setValue('enabledForCheckDebits', currentPlatform.transferApprovalConfig?.enabledForCheckDebits ?? false);
    setValue(
      'enabledForInternationalWire',
      currentPlatform.transferApprovalConfig?.enabledForInternationalWire ?? false
    );
    setValue('enabledForWire', currentPlatform.transferApprovalConfig?.enabledForWire ?? false);
    setValue('enabledForRealtime', currentPlatform.transferApprovalConfig?.enabledForRealtime ?? false);
  }, [currentPlatform]);
  useEffect(() => {
    if (!intlWireConfig) return;
    setValue('fixedFee', intlWireConfig.fixedFee);
    setValue('fxRateMarginBps', intlWireConfig.fxRateMarginBps);
  }, [intlWireConfig]);
  useEffect(() => {
    if (!revenueBankAccountResponse) return;
    setValue('revenueAccountNumberId', revenueBankAccountResponse?.id);
  }, [revenueBankAccountResponse]);

  const handleSave = useCallback(
    async (data: PlatformSettingsFields) => {
      if (!platformId || !currentUser) {
        return;
      }

      const updateFns: Promise<any>[] = [
        currentPlatform?.id === platformId
          ? currentPlatform.update({ name: data.name, reportingTimeZone: data.reportingTimeZone })
          : PlatformRepository.update({
              platformId,
              name: data.name,
              reportingTimeZone: data.reportingTimeZone,
            }),
      ];

      if (data.revenueAccountNumberId || data.fixedFee || data.fxRateMarginBps) {
        if (!data.revenueAccountNumberId) {
          addDangerNotification({
            content: 'Please select a valid revenue account number',
          });
          return;
        }
        const { data: revenueBankAccount } = await bankAccountGetBankAccount({
          path: { bank_account_id: data.revenueAccountNumberId },
          throwOnError: true,
        });
        const revenueAccountNumberId = revenueBankAccount?.default_account_number_id;
        if (!revenueAccountNumberId) {
          addDangerNotification({
            content: 'Please select a valid revenue account number',
          });
          return;
        }
        updateFns.push(
          PlatformRepository.updateInternationalWireConfig({
            revenueAccountNumberId,
            fixedFee: data.fixedFee,
            fxRateMarginBps: data.fxRateMarginBps,
            platformId,
          })
        );
      }

      if (enableTransferApprovals && canConfigureTransferApprovals) {
        updateFns.push(
          PlatformRepository.updateConfig({
            transferApprovalConfig: {
              enabledForAchCredits: data.enabledForAchCredits,
              enabledForAchDebits: data.enabledForAchDebits,
              enabledForCheckDebits: data.enabledForCheckDebits,
              enabledForInternationalWire: data.enabledForInternationalWire,
              enabledForWire: data.enabledForWire,
              // Omitted when the flag is off so saving never clears a config set out-of-band.
              ...(enableRealtimeTransferApprovals ? { enabledForRealtime: data.enabledForRealtime } : {}),
            },
            platformId,
          })
        );
      }

      Promise.all(updateFns)
        .then(() => {
          addSuccessNotification({
            content: 'Platform saved',
          });
          loadPlatform({ fetchMeta: true });
        })
        .catch((error) => {
          addDangerNotification({
            content: error.message,
          });
        });
    },
    [
      addSuccessNotification,
      addDangerNotification,
      currentUser,
      currentPlatform,
      enableTransferApprovals,
      enableRealtimeTransferApprovals,
      canConfigureTransferApprovals,
      platformId,
    ]
  );

  const fetchInit = () => {
    if (currentUser && platformId) {
      getPlatformIntlWireConfig({ platformId });
    }
  };
  useEffect(fetchInit, [platformId]);

  useEffect(
    () =>
      useSessionStore.subscribe(
        (state) => ({
          isLoading: state.isLoading,
          defaultPlatformId: state.currentUser?.defaultPlatformId,
        }),
        () => fetchInit(),
        {
          fireImmediately: true,
        }
      ),
    []
  );

  useEffect(() => {
    import('~/data/timezones.json').then((json) => {
      const entries: DropdownOption[] = Object.keys(json.default).map((t) => ({
        label: t,
        small: json.default[t as keyof typeof json.default],
        value: t,
      }));

      entries.splice(1, 0, {
        isDivider: true,
      });

      setTimezones(entries);
    });
  }, []);

  const entityName = getEntityName(rootEntity ?? undefined);
  const entityIcon = getEntityIcon(rootEntity ?? undefined);

  return (
    <>
      <Loading
        isLoading={
          (!intlWireConfig && isIntlWireConfigLoading && !revenueBankAccountResponse && isRevenueBankAccountLoading) ||
          !currentPlatform
        }
      >
        <FormProvider {...formMethods}>
          <form autoComplete="off" onSubmit={handleSubmit(handleSave)}>
            {RenderFields<PlatformSettingsFields>({
              sections: [
                {
                  headline: 'General',
                  fields: [
                    {
                      id: 'rootEntity',
                      label: 'Root Entity',
                      children: () => {
                        if (rootEntityNotFound) {
                          return (
                            <Button
                              onClick={() => navigate(ROUTE.ENTITIES_ROOT_ENTITY)}
                              size="small"
                              icon={<Icon.ArrowUpRight />}
                              iconRight
                              type="button"
                            >
                              Create Root Entity
                            </Button>
                          );
                        }
                        return (
                          <ObjectCard
                            error={rootEntityError ? 'Failed to load root entity' : undefined}
                            icon={entityIcon}
                            isLoading={isRootEntityLoading}
                            primaryText={entityName || 'Unnamed Entity'}
                            id={{ label: 'ID:', value: rootEntity?.id ?? '' }}
                            onClick={() => navigate(ROUTE.ENTITIES_ROOT_ENTITY)}
                          />
                        );
                      },
                    },
                    {
                      id: 'name',
                      label: 'Name',
                      rules: { required: true },
                      children: ({ value, onChange, onBlur, ref }, { isTouched, error }) => {
                        return (
                          <Input
                            value={String(value)}
                            onChange={onChange}
                            onBlur={onBlur}
                            ref={ref}
                            placeholder="Name"
                            hasError={isTouched && !!error}
                          />
                        );
                      },
                    },
                    {
                      id: 'id',
                      label: 'ID',
                      children: ({ value }) => {
                        return <CopyInput value={(value as string) ?? ''} isMonospaced />;
                      },
                    },
                    {
                      id: 'reportingTimeZone',
                      label: 'Reporting Time Zone',
                      children: ({ value, onChange }) => {
                        return <Dropdown fullWidth active={value} onChange={onChange} options={timezones} search />;
                      },
                    },
                    {
                      id: 'createdAt',
                      label: 'Created',
                      children: ({ value }) => (
                        <FormText>{value ? formatDateTime(value as Date, { timeZoneFormat: 'utc' }) : '-'}</FormText>
                      ),
                    },
                    {
                      id: 'platformLevel',
                      label: 'Platform Level',
                      children: ({ value }) => <Chip size="large">{value ? formatString(value as string) : ''}</Chip>,
                    },
                  ],
                },
                {
                  headline: 'International Wire Billing Configuration',
                  fields: [
                    {
                      id: 'revenueAccountNumberId',
                      label: 'Revenue Account Number',
                      tooltip: 'Account number ID for international wire revenues of a platform',
                      children: ({ value, onChange }) => (
                        <BankAccountDropdown value={value as string} onChange={onChange} fullWidth maxWidth="640px" />
                      ),
                    },
                    {
                      id: 'fixedFee',
                      label: 'Fixed Fee',
                      children: ({ value, onChange }) => {
                        return (
                          <StyledAmountInput
                            value={value as number | undefined}
                            onChange={onChange}
                            placeholder="Fixed Fee"
                            currencyList={CurrenciesUSD}
                            fixedCurrencyCode="USD"
                            currencyCode="USD"
                          />
                        );
                      },
                    },
                    {
                      id: 'fxRateMarginBps',
                      label: 'FX Rate Margin BPS',
                      tooltip: 'Number of basis points (BPS) for FX margin. 1 bps = 0.01%',
                      children: ({ value, onChange }) => {
                        return (
                          <NumberInput
                            value={value ? String(value) : ''}
                            onChange={(newValue) => onChange(Number(newValue))}
                            placeholder="FX Rate Margin BPS"
                          />
                        );
                      },
                    },
                  ],
                },
                {
                  headline: 'Transfer Approval Configuration',
                  description:
                    'Configures transfer types which require approval. Transfers may be approved by users with the "Approve Transfers" permission on their role.',
                  hide: !enableTransferApprovals,
                  fields: [
                    {
                      id: 'enabledForAchCredits',
                      label: 'ACH Credit Transfers',
                      children: ({ value, onChange }) => (
                        <Inner p={0} pt={8}>
                          <Tooltip
                            content={TRANSFER_APPROVALS_SUPPORT_TOOLTIP}
                            isOpen={canConfigureTransferApprovals ? false : undefined}
                            placement="right"
                          >
                            <ToggleTooltipTarget>
                              <Toggle
                                isChecked={typeof value === 'boolean' ? value : false}
                                isDisabled={!canConfigureTransferApprovals}
                                onCheckedChange={onChange}
                              />
                            </ToggleTooltipTarget>
                          </Tooltip>
                        </Inner>
                      ),
                    },
                    {
                      id: 'enabledForAchDebits',
                      label: 'ACH Debit Transfers',
                      children: ({ value, onChange }) => (
                        <Inner p={0} pt={8}>
                          <Tooltip
                            content={TRANSFER_APPROVALS_SUPPORT_TOOLTIP}
                            isOpen={canConfigureTransferApprovals ? false : undefined}
                            placement="right"
                          >
                            <ToggleTooltipTarget>
                              <Toggle
                                isChecked={typeof value === 'boolean' ? value : false}
                                isDisabled={!canConfigureTransferApprovals}
                                onCheckedChange={onChange}
                              />
                            </ToggleTooltipTarget>
                          </Tooltip>
                        </Inner>
                      ),
                    },
                    {
                      id: 'enabledForCheckDebits',
                      label: 'Issued Checks',
                      children: ({ value, onChange }) => (
                        <Inner p={0} pt={8}>
                          <Tooltip
                            content={TRANSFER_APPROVALS_SUPPORT_TOOLTIP}
                            isOpen={canConfigureTransferApprovals ? false : undefined}
                            placement="right"
                          >
                            <ToggleTooltipTarget>
                              <Toggle
                                isChecked={typeof value === 'boolean' ? value : false}
                                isDisabled={!canConfigureTransferApprovals}
                                onCheckedChange={onChange}
                              />
                            </ToggleTooltipTarget>
                          </Tooltip>
                        </Inner>
                      ),
                    },
                    {
                      id: 'enabledForInternationalWire',
                      label: 'International Wire Transfers',
                      children: ({ value, onChange }) => (
                        <Inner p={0} pt={8}>
                          <Tooltip
                            content={TRANSFER_APPROVALS_SUPPORT_TOOLTIP}
                            isOpen={canConfigureTransferApprovals ? false : undefined}
                            placement="right"
                          >
                            <ToggleTooltipTarget>
                              <Toggle
                                isChecked={typeof value === 'boolean' ? value : false}
                                isDisabled={!canConfigureTransferApprovals}
                                onCheckedChange={onChange}
                              />
                            </ToggleTooltipTarget>
                          </Tooltip>
                        </Inner>
                      ),
                    },
                    {
                      id: 'enabledForRealtime',
                      label: 'Realtime Transfers',
                      hide: !enableRealtimeTransferApprovals,
                      children: ({ value, onChange }) => (
                        <Inner p={0} pt={8}>
                          <Tooltip
                            content={TRANSFER_APPROVALS_SUPPORT_TOOLTIP}
                            isOpen={canConfigureTransferApprovals ? false : undefined}
                            placement="right"
                          >
                            <ToggleTooltipTarget>
                              <Toggle
                                isChecked={typeof value === 'boolean' ? value : false}
                                isDisabled={!canConfigureTransferApprovals}
                                onCheckedChange={onChange}
                              />
                            </ToggleTooltipTarget>
                          </Tooltip>
                        </Inner>
                      ),
                    },
                    {
                      id: 'enabledForWire',
                      label: 'Wire Transfers',
                      children: ({ value, onChange }) => (
                        <Inner p={0} pt={8}>
                          <Tooltip
                            content={TRANSFER_APPROVALS_SUPPORT_TOOLTIP}
                            isOpen={canConfigureTransferApprovals ? false : undefined}
                            placement="right"
                          >
                            <ToggleTooltipTarget>
                              <Toggle
                                isChecked={typeof value === 'boolean' ? value : false}
                                isDisabled={!canConfigureTransferApprovals}
                                onCheckedChange={onChange}
                              />
                            </ToggleTooltipTarget>
                          </Tooltip>
                        </Inner>
                      ),
                    },
                  ],
                },
              ],
            })}

            <Inner px="var(--page-margin)">
              <Action>
                <Button type="submit" size="small">
                  Save changes
                </Button>
              </Action>
            </Inner>
          </form>
        </FormProvider>
      </Loading>
    </>
  );
};
